import React, { useLayoutEffect, useRef } from 'react';
import styled, { CSSProperties } from 'styled-components';
import { ItimelineItem } from '@typings/pages/timeline.interface';
import { useRecoilState } from 'recoil';
import { modalOpenState } from '@atom/modal/modal';
import UpdateBoardModal from '../UpdateBoard/UpdateBoardModal';
import { useCollection } from '@nandorojo/swr-firestore';
import { IcommentItem } from '@typings/common/comment.interface';
import TimelineBoardContents from '@template/BoardContents/TimelineBoardContents';
import CommentQuill, {
  initialToolbar,
} from '@components/library/Quill/CommentQuill';
import CommentList from '@template/Comment/CommentList';
import { scrollbarStyle } from '@styles/variants/scrollbar';
import { AUTHOR_AVATAR_TYPE, AUTHOR_DISPLAY_TYPE } from '@typings/posting/postingSetting.interface';

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  height: 100%;

  position: relative;
`;
const CommentContainer = styled.div`
  padding: 16px;
  ${scrollbarStyle};
  /* 브라우저 자동 앵커 이동 방지: 데이터 로드/이미지 로딩 시 점프 억제 */
  overflow-anchor: none;
`;
const ContentsWrap = styled.div`
  width: 100%;
  padding: 16px;
  border-bottom: 1px solid var(--lineColor);
`;

const AddCommentWrap = styled.div`
  width: 100%;
  padding: 16px;
  border-top: 1px solid var(--lineColor);
`;

type Props = {
  boardData: ItimelineItem | any;
  collectionPath: string;
  docId: string;
  showCommentIcon?: boolean;
  listWrapStyle?: CSSProperties;
  isReplyDetail?: boolean;
  showReply?: boolean;
  isRelateBoard?: boolean;
  authorDisplayType?: AUTHOR_DISPLAY_TYPE;
  authorAvatarType?: AUTHOR_AVATAR_TYPE;
  hideImg?: boolean;
};
function TimelineDetailContents({
  boardData,
  docId,
  collectionPath,
  showCommentIcon,
  isReplyDetail,
  showReply,
  isRelateBoard = true,
  authorDisplayType = 'user',
  authorAvatarType = 'account',
  hideImg = false,
}: Props) {
  const commentRef = useRef<HTMLDivElement>(null);
  const [openModal, setOpenModal] = useRecoilState(modalOpenState);
  const { data: commentData } = useCollection<IcommentItem>(
    `${collectionPath}Comment`,
    {
      listen: true,
      where: ['boardId', '==', docId], // 복합색인 필요
      orderBy: ['createdAt', 'asc'],
    },
  );

  if (!boardData) return null;

  const {
    authorUid,
    id: boardId,
    isItemCraft,
    itemCraftMaterials,
    luckyDrawResult,
    botResults,
    isInvestigation,
    investigationAreaId,
    investigationAreaName,
    investigationResult,
    giftResult,
  } = boardData;

  // 상세 진입/문서 변경 시 스크롤 최상단 고정
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    commentRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [docId]);

  return (
    <Container>
      <ContentsWrap>
        <TimelineBoardContents
          data={boardData}
          collectionPath={collectionPath}
          shape="transparent"
          showImg={!hideImg}
          // 아이템 조합
          isItemCraft={isItemCraft}
          itemCraftMaterials={itemCraftMaterials}
          authorUid={authorUid}
          postId={boardId || docId}
          // 랜덤박스 결과
          luckyDrawResult={luckyDrawResult}
          // 커스텀 봇 결과
          botResults={botResults}
          // 조사하기
          isInvestigation={isInvestigation}
          investigationAreaId={investigationAreaId}
          investigationAreaName={investigationAreaName}
          investigationResult={investigationResult}
          // 선물하기
          giftResult={giftResult}
          // 작성자 표시 타입
          authorDisplayType={authorDisplayType}
          authorAvatarType={authorAvatarType}
        />
      </ContentsWrap>

      <CommentContainer ref={commentRef}>
        <CommentList
          collectionPath={`${collectionPath}`}
          boardId={docId}
          boardAuthorUid={boardData.authorUid || ''}
          wrapStyle="transparent"
          showReply
          authorDisplayType={authorDisplayType}
        />
      </CommentContainer>

      <AddCommentWrap>
        <CommentQuill
          boardId={docId}
          boardAuthorUid={boardData.authorUid || ''}
          placeholder="댓글 입력"
          setToolbar={initialToolbar}
          type="input"
          collectionPath={collectionPath}
          id={`posting-comment-${docId}`}
        />
      </AddCommentWrap>

      {openModal && <UpdateBoardModal />}
    </Container>
  );
}

export default TimelineDetailContents;
